// Noise suppression for the voice sent in a call: DeepFilterNet3 as wasm in an
// AudioWorklet, between the microphone and what the connections send.
//   mic → MediaStreamAudioSourceNode → AudioWorkletNode (public/df/denoise.worklet.js)
//       → MediaStreamAudioDestinationNode → its track goes to every connection
//
// Promise:
// - The wasm (about 9 MB) and the model (about 8 MB) come from this origin's
//   /df/ and are compiled once per session (`preload` starts it while the
//   microphone is being asked for). A failure is not kept: the next join
//   tries again.
// - Where it cannot run (no AudioWorklet, the assets fail, the worklet does not
//   say ready within 10 seconds, a processor error) `create` answers null and
//   the call sends the plain microphone.
// - Turning it off passes the sound through without taking the path down, so
//   nothing is renegotiated.
// - The model file is named .bin: served as .gz, static servers add
//   `Content-Encoding: gzip`, the browser unpacks it, and the wasm (which
//   expects the tar.gz) panics.
import { defineDeviceSetting } from "@trameria/ui-core";
import type { CallAudio } from "./audio";

export interface Denoiser {
  /** The processed sound (sent, and measured for speaking). */
  stream: MediaStream;
  /** false passes the sound through (the path stays, no renegotiation). */
  setEnabled(on: boolean): void;
  close(): void;
}

export type CreateDenoiser = (mic: MediaStream) => Promise<Denoiser | null>;

const WASM_URL = "/df/df_bg.wasm";
const MODEL_URL = "/df/DeepFilterNet3_onnx.bin";
const WORKLET_URL = "/df/denoise.worklet.js";
/** DeepFilterNet's attenuation limit in dB (100: none). */
const ATTEN_LIM_DB = 100;
const READY_TIMEOUT_MS = 10_000;

/** Whether noise suppression is wanted, kept on this device (on unless turned off). */
export const denoiseSetting = defineDeviceSetting<boolean>({
  key: "trameria-voice-denoise",
  parse: (raw) => raw !== "off",
  serialize: (on) => (on ? "on" : "off"),
});

interface Assets {
  module: WebAssembly.Module;
  model: ArrayBuffer;
}

const supported = () =>
  typeof AudioContext !== "undefined" &&
  typeof AudioWorkletNode !== "undefined" &&
  typeof WebAssembly !== "undefined";

export interface DenoiseLoader {
  /** Starts fetching the assets (during the microphone's question). */
  preload(): void;
  create: CreateDenoiser;
}

export function denoiseLoader(audio: CallAudio): DenoiseLoader {
  let assets: Promise<Assets | null> | null = null;
  const loaded = new WeakMap<AudioContext, Promise<void>>();

  const fetchAssets = async (): Promise<Assets | null> => {
    try {
      const [module, model] = await Promise.all([
        (async () => {
          const res = await fetch(WASM_URL);
          if (!res.ok) throw new Error(`wasm ${res.status}`);
          // compileStreaming needs the application/wasm type; fall back.
          try {
            return await WebAssembly.compileStreaming(res.clone());
          } catch {
            return await WebAssembly.compile(await res.arrayBuffer());
          }
        })(),
        (async () => {
          const res = await fetch(MODEL_URL);
          if (!res.ok) throw new Error(`model ${res.status}`);
          return await res.arrayBuffer();
        })(),
      ]);
      return { module, model };
    } catch (error) {
      console.warn("voice: noise suppression assets failed", error);
      assets = null;
      return null;
    }
  };

  return {
    preload() {
      if (supported()) assets ??= fetchAssets();
    },
    async create(mic) {
      if (!supported()) return null;
      const ac = audio.ensure();
      if (!ac) return null;
      assets ??= fetchAssets();
      const got = await assets;
      if (!got) return null;
      try {
        let ready = loaded.get(ac);
        if (!ready) {
          ready = ac.audioWorklet.addModule(WORKLET_URL);
          loaded.set(ac, ready);
        }
        await ready;
      } catch (error) {
        console.warn("voice: noise suppression worklet failed", error);
        loaded.delete(ac);
        return null;
      }
      const node = new AudioWorkletNode(ac, "df-denoise", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: {
          module: got.module,
          // Copied by structured clone (the original is kept for the next).
          model: got.model,
          attenLim: ATTEN_LIM_DB,
        },
      });
      const ready = new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => resolve(false), READY_TIMEOUT_MS);
        node.port.onmessage = (e) => {
          if (e.data?.type === "ready") {
            clearTimeout(timer);
            resolve(true);
          } else if (e.data?.type === "error")
            console.warn("voice: noise suppression failed", e.data.message);
        };
        node.onprocessorerror = () => {
          clearTimeout(timer);
          resolve(false);
        };
      });
      const source = ac.createMediaStreamSource(mic);
      const dest = ac.createMediaStreamDestination();
      source.connect(node).connect(dest);
      const close = () => {
        node.port.postMessage({ type: "close" });
        source.disconnect();
        node.disconnect();
        for (const t of dest.stream.getTracks()) t.stop();
      };
      if (!(await ready)) {
        close();
        return null;
      }
      return {
        stream: dest.stream,
        setEnabled: (on) =>
          node.port.postMessage({ type: "enabled", value: on }),
        close,
      };
    },
  };
}
